import { captureException } from '@sentry/react';
import { isOneOf } from 'common/typeUtils';
import { appConfig } from 'modules/config/appConfig';
import type { PostHog } from 'posthog-js';
import type { CustomerResponse } from './api.types';

const enableAnalytics = isOneOf(appConfig.environment, ['prod', 'uat']);

/**
 * Lazy-load posthog client only when events are used to avoid having it part of every bundle.
 * That way it costs us nothing to have it available in the codebase.
 */
let _posthogPromise: Promise<PostHog> | null = null;
const lazyLoadClient = async () => {
  if (_posthogPromise) {
    return _posthogPromise;
  }
  _posthogPromise = import('posthog-js')
    .then(({ posthog }) => {
      posthog.init('phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq', {
        api_host: 'https://eu.i.posthog.com',
        persistence: 'memory',
        // disable all auto capture
        autocapture: false,
        capture_pageview: false,
        capture_performance: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_pageleave: false,
        enable_recording_console_log: false,
        advanced_disable_feature_flags: true,
        before_send: (payload) => {
          if (payload?.properties) {
            payload.properties.consent_given = getHasGivenConsent();
          }
          return enableAnalytics ? payload : null;
        },
      });
      return posthog;
    })
    .catch((e) => {
      _posthogPromise = null; // allow retry on next call
      throw e; // re-throw so callers still see the rejection
    });
  return _posthogPromise;
};

/**
 * Remove previously stored deviceId from when we did not use posthogs own library
 */
localStorage.removeItem('rtv_deviceId');

let identifiedUser: CustomerResponse | undefined;
let userSoftwareProducts: string[] = [];

export const identifyUserForProductAnalytics = (customer: CustomerResponse) => {
  identifiedUser = customer;
  captureUXEvent('mypage_visit', {
    customerStatus: customer.status,
    customerClass: customer.customerClass,
    customerType: customer.customerType,
  });
};
export const setUserProducts = (products: string[]) => {
  userSoftwareProducts = products;
};

export type PostHogMissingInfoEvent =
  | 'missing-contact-info-form-shown'
  | 'missing-contact-info-form-dismissed'
  | 'missing-contact-info-form-dismissed-snoozed'
  | 'missing-contact-info-form-submitted'
  | 'missing-contact-info-success-dismissed'
  | 'missing-contact-info-success-dismissed-link'
  | 'missing-contact-info-success-dismissed-close';

/* ADD names of events here as needed */
type PostHogEvent =
  | 'post cancellation form'
  | 'click-tv2-upgrade-link'
  | 'click-pl-campaign-alert'
  | 'click-change-password'
  | 'click-link-player-settings'
  | 'click-change-pin'
  | 'submit-change-pin'
  | 'click-forgot-pin'
  | 'toggle-purchase-pin'
  | 'order-products'
  | 'activate-products'
  | 'mypage_visit'
  | 'mypage_campaign_keyword_missing'
  | 'view-viaplay-info-page'
  | 'select viaplay compensation'
  | 'reset viaplay compensation'
  | 'click activate tv2 favoritter'
  | 'click-viapla-context-box'
  | 'enter-my-profile-page'
  | 'enter-change-my-profile-page'
  | 'submit-my-profile-form'
  | 'alacarte-saved-preferred-channels'
  | 'click-activate-viaplay'
  | 'click-activate-hbo-max'
  | 'click-reactivate-hbo-max'
  | 'product-churn-profiling'
  | 'click-order-viaplay-access'
  | 'product-regrade'
  | 'click-order-hbo-max-access'
  | 'click-order-tv2-access'
  | 'campaign_click_next'
  | 'campaign_click_complete_order'
  | PostHogMissingInfoEvent;

type CaptureFn = (event: PostHogEvent, properties?: Record<string, unknown>) => void;
export const captureUXEvent: CaptureFn = (event, properties) => {
  const _props = Object.assign({}, properties, { env: appConfig.environment });
  /** Assign properties to the user profile */
  if (identifiedUser) {
    _props.distinct_id = identifiedUser.id.toString();
    // Sets "person properties" on the user profile in posthog
    if (isOneOf(event, ['post cancellation form', 'click-tv2-upgrade-link'])) {
      _props.$set = {
        name: identifiedUser.firstName,
        softwareProducts: userSoftwareProducts,
        customerType: identifiedUser.customerType,
        customerClass: identifiedUser.customerClass,
        customerStatus: identifiedUser.status,
      };
    }
  }

  lazyLoadClient()
    .then((posthog) => {
      posthog.capture(event, _props);
    })
    .catch((e) => {
      captureException(new Error('Failed to capture posthog event', { cause: e }), {
        fingerprint: ['posthog-capture-failure'],
        extra: { event },
      });
    });
};

function getHasGivenConsent(): boolean {
  try {
    return (
      document.cookie
        .split('; ')
        .find((c) => c.startsWith('wscrCookieConsent'))
        ?.includes('3=true') ?? false
    );
  } catch {
    return false;
  }
}
